import {GoalType, NonLifestyleGoalType, PrioritizationType} from "../models/GoalType";
import {BeneficiaryGoals, GoalModelType} from "../models/GoalModelType";
import {InvestorGroupType} from "../../ClientManagement/models/InvestorGroupType";
import GoalUtils from "../Summary/GoalUtils";

export interface NonLifestyleGoalRow {
    name: string,
    goalType?: GoalType,
    annualFlow?: number,
    yearsOfFlow: number,
    yearsUntilFlow: number,
    presentValue: number,
    beneficiaryName: string,
    isFundedByNonInvestableAssets: boolean,
    savedGoal?: NonLifestyleGoalType,
    fundedByPortfolioPercentage?: number
}

export interface FundedByPortfolioPercentageAttributes {
    breakEvenYear: number,
    breakEvenYearTotalPV: number,
    remainingAssetsForBreakEvenYear: number
}

function makeNonLifestyleGoalListTableRowData(
    nonLifestyleGoalsForDisplay: NonLifestyleGoalType[],
    beneficiaryGoalsForDisplay: BeneficiaryGoals[],
    investorGroup: InvestorGroupType
): NonLifestyleGoalRow[] {
    const nonLifestyleGoalRows: NonLifestyleGoalRow[] = nonLifestyleGoalsForDisplay.map(
        (goal: NonLifestyleGoalType) => {
            const {yearsOfFlow, yearsUntilFlow} = GoalUtils.getYearsOfFlowAndYearsUntilFlow(goal, investorGroup)

            return {
                name: goal.userInputs.description,
                goalType: goal.goalType,
                annualFlow: goal.userInputs.annualFlow,
                presentValue: goal.calculatedFields.presentValue,
                beneficiaryName: "",
                isFundedByNonInvestableAssets: goal.userInputs.isFundedByNonInvestableAssets,
                yearsOfFlow,
                yearsUntilFlow,
                savedGoal: goal
            }
        }
    );

    const beneficiaryGoalRowsPerBeneficiary: NonLifestyleGoalRow[][] = Object.values(beneficiaryGoalsForDisplay).map(
        (beneficiaryGoal: BeneficiaryGoals) => {
            const beneficiaryName: string = beneficiaryGoal.name;

            return Object.values(beneficiaryGoal.goals).map(
                (goal: NonLifestyleGoalType) => {

                    const {
                        yearsOfFlow,
                        yearsUntilFlow
                    } = GoalUtils.getYearsOfFlowAndYearsUntilFlow(goal, investorGroup)

                    return {
                        name: goal.userInputs.description,
                        goalType: GoalType.FAMILY,
                        annualFlow: goal.userInputs.annualFlow,
                        presentValue: goal.calculatedFields.presentValue,
                        isFundedByNonInvestableAssets: goal.userInputs.isFundedByNonInvestableAssets,
                        beneficiaryName,
                        yearsOfFlow,
                        yearsUntilFlow,
                        savedGoal: goal
                    }
                }
            );
        }
    )
    const familyGoalRows: NonLifestyleGoalRow[] = [];
    beneficiaryGoalRowsPerBeneficiary.forEach(
        (beneficiaryGoalRows: NonLifestyleGoalRow[]) => {
            familyGoalRows.push(...beneficiaryGoalRows);
        }
    );
    return [...nonLifestyleGoalRows, ...familyGoalRows];
}

export function sortNonLifeStyleGoalList(rowData: NonLifestyleGoalRow[]) {
    const rowDataForSort = rowData.map((row) => {
            return {
                ...row,
                sumYearsOfFlowAndYearsUntilFlow: row.yearsOfFlow + row.yearsUntilFlow
            }
        }
    )
    const fieldSorter = (fields: string[]) => (a: NonLifestyleGoalRow, b: NonLifestyleGoalRow) => fields.map(o => {
        let dir = 1;
        if (o[0] === '-') {
            dir = -1;
            o = o.substring(1);
        }
        let nonLifestyleGoalRowProperty = o as keyof typeof a
        // @ts-ignore
        if (a[nonLifestyleGoalRowProperty] > b[nonLifestyleGoalRowProperty]) {
            return dir;
        }
        // @ts-ignore
        return a[nonLifestyleGoalRowProperty] < b[nonLifestyleGoalRowProperty] ? -(dir) : 0;

    }).reduce((p, n) => p ? p : n, 0);
    rowDataForSort.sort(fieldSorter(['sumYearsOfFlowAndYearsUntilFlow', 'yearsUntilFlow', '-presentValue', 'name']));
    return rowDataForSort.map((row) => {
            return {
                name: row.name,
                goalType: row.goalType,
                annualFlow: row.annualFlow,
                yearsOfFlow: row.yearsOfFlow,
                yearsUntilFlow: row.yearsUntilFlow,
                presentValue: row.presentValue,
                beneficiaryName: row.beneficiaryName,
                isFundedByNonInvestableAssets: row.isFundedByNonInvestableAssets,
                savedGoal: row.savedGoal
            }
        }
    )
}

export function getDisplayPresentValueForNonLifestyleGoal(goalRow: NonLifestyleGoalRow, enableEnhancedLifestyleFunding: boolean): number {
    if (!enableEnhancedLifestyleFunding || goalRow.isFundedByNonInvestableAssets || !goalRow.savedGoal) {
        return goalRow.presentValue;
    }

    const {presentValueFundedByPortfolio, investableShortfall} = goalRow.savedGoal.calculatedFields;
    return (presentValueFundedByPortfolio ?? 0) + (investableShortfall ?? 0);
}

export function addFbniLifestyleGoal(
    goalModel: GoalModelType,
    fundedByNonInvestables: NonLifestyleGoalRow[],
    enableEnhancedLifestyleFunding: boolean = false
): NonLifestyleGoalRow[] {
    const lifestyleCalculatedFields = goalModel.lifestyleSpendingGoal.calculatedFields;
    const lifestylePresentValueFundedByNonInv = enableEnhancedLifestyleFunding
        ? lifestyleCalculatedFields.fundedByNonInvestable
        : lifestyleCalculatedFields.presentValueForInsufficientYears;

    if ((lifestylePresentValueFundedByNonInv ?? 0) <= 0) {
        return fundedByNonInvestables;
    }

    const sufficientYears = lifestyleCalculatedFields.sufficientYears;
    const planningPeriod = goalModel.investorGroup.planningPeriod.numberOfYears;

    const fbniLifeStyleGoal: NonLifestyleGoalRow = {
        beneficiaryName: "",
        isFundedByNonInvestableAssets: false,
        name: "Lifestyle Spending",
        presentValue: lifestylePresentValueFundedByNonInv ?? 0,
        yearsUntilFlow: sufficientYears,
        yearsOfFlow: planningPeriod - sufficientYears
    }

    fundedByNonInvestables.unshift(fbniLifeStyleGoal)
    return fundedByNonInvestables;
}

export function getNeeds(
    fundedByPortfolioGoals: NonLifestyleGoalRow[],
    goalModel: GoalModelType,
    enableEnhancedLifestyleFunding: boolean
) {
    let needs: NonLifestyleGoalRow[] = sortNonLifeStyleGoalList(fundedByPortfolioGoals.filter(s => s.savedGoal!.prioritizationType === PrioritizationType.NEEDS))
    const totalNeedsPresentValue = getNonLifestyleGoalTotalPresentValue(needs);
    const availableAssetsForNeeds = goalModel.assets.totalInvestableValue
        - goalModel.taxLiabilities.totalTaxLiabilitiesFundedByPortfolio
        - goalModel.lifestyleSpendingGoal.calculatedFields.requiredPortfolioSpendingPresentValue
        - (enableEnhancedLifestyleFunding ? goalModel.lifestyleSpendingGoal.calculatedFields.presentValueForInsufficientYears : 0);
    needs = addFundedByPortfolioPercentageToNeedsOrWants(needs, availableAssetsForNeeds, totalNeedsPresentValue);
    return {needs, totalNeedsPresentValue, availableAssetsForNeeds};
}

export function getWants(fundedByPortfolioGoals: NonLifestyleGoalRow[], availableAssetsForNeeds: number, totalNeedsPresentValue: number) {
    let wants: NonLifestyleGoalRow[] = sortNonLifeStyleGoalList(fundedByPortfolioGoals.filter(s => s.savedGoal!.prioritizationType === PrioritizationType.WANTS))
    const totalWantsPresentValue = getNonLifestyleGoalTotalPresentValue(wants);
    const availableAssetsForWants = availableAssetsForNeeds - totalNeedsPresentValue;
    return addFundedByPortfolioPercentageToNeedsOrWants(wants, availableAssetsForWants, totalWantsPresentValue);
}

export function addFundedByPortfolioPercentageToNeedsOrWants(nonLifestyleGoalRows: NonLifestyleGoalRow[], availableAssets: number, totalPVPerPrioritizationType: number): NonLifestyleGoalRow[] {
    if (availableAssets <= 0) {
        return nonLifestyleGoalRows.map(nonLifestyleGoalRow => {
            nonLifestyleGoalRow.fundedByPortfolioPercentage = 0;
            return nonLifestyleGoalRow
        })
    }
    if (totalPVPerPrioritizationType > availableAssets) {
        const fundedByPortfolioPercentageAttr = getFundedByPortfolioPercentageAttributes(nonLifestyleGoalRows, availableAssets);
        return updateNonLSRowsWithFundedByPortfolioPercentage(nonLifestyleGoalRows, fundedByPortfolioPercentageAttr)
    }

    return nonLifestyleGoalRows.map(nonLifestyleGoalRow => {
        nonLifestyleGoalRow.fundedByPortfolioPercentage = 100;
        return nonLifestyleGoalRow
    })
}

export function getNonLifestyleGoalTotalPresentValue(nonLifestyleGoals: NonLifestyleGoalRow[]): number {
    return nonLifestyleGoals
        .map((nonLifestyleGoal) => nonLifestyleGoal.presentValue)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
}


function get2dArrayOfPVs(prioritizationRows: NonLifestyleGoalRow[]): number[][] {
    let twoDArray: number[][];
    // Create 2d array of pvs each row representing pv per year for a specific goals
    twoDArray = prioritizationRows.map(ele => ele.savedGoal?.calculatedFields.presentValueByYear!);

    // creating normalized 2d array with padded zeros to make each row of equal length
    const maxLength = Math.max(...(twoDArray.map(el => el.length)));
    twoDArray = twoDArray.map(ele => ele.concat(Array(maxLength - ele.length).fill(0)))

    return twoDArray;
}

export function getFundedByPortfolioPercentageAttributes(prioritizationRows: NonLifestyleGoalRow[], assetsAvailable: number): FundedByPortfolioPercentageAttributes {
    const presentValues2dArrayWithPaddedZeros = get2dArrayOfPVs(prioritizationRows);
    const maxLength = Math.max(...(presentValues2dArrayWithPaddedZeros.map(el => el.length)));
    for (let i = 0; i < maxLength; i++) {
        let totalPvForAllGoalsPerYear = sumColumnValues(presentValues2dArrayWithPaddedZeros, i);
        if (assetsAvailable > totalPvForAllGoalsPerYear) {
            assetsAvailable = assetsAvailable - totalPvForAllGoalsPerYear;
        } else {
            return {
                breakEvenYear: i,
                breakEvenYearTotalPV: totalPvForAllGoalsPerYear,
                remainingAssetsForBreakEvenYear: assetsAvailable
            }
        }
    }

    return {breakEvenYear: 0, breakEvenYearTotalPV: 0, remainingAssetsForBreakEvenYear: 0}
}

export function sumColumnValues(arr: number[][], columnIndex: number): number {
    let sum = 0;
    for (const element of arr) {
        const row = element;
        if (columnIndex < row.length) {
            sum += row[columnIndex];
        }
    }
    return sum;
}

function updateNonLSRowsWithFundedByPortfolioPercentage(nonLifestyleGoalRows: NonLifestyleGoalRow[], fundedByPortfolioPercentageAttributes: FundedByPortfolioPercentageAttributes) {
    return nonLifestyleGoalRows.map(nonLSGoalRow => {
        const {
            breakEvenYear,
            breakEvenYearTotalPV,
            remainingAssetsForBreakEvenYear
        } = fundedByPortfolioPercentageAttributes;

        const presentValuePerYear = nonLSGoalRow.savedGoal?.calculatedFields.presentValueByYear;
        if (nonLSGoalRow.presentValue > 0 && presentValuePerYear && presentValuePerYear.length > breakEvenYear) {
            const breakEvenYearPV = presentValuePerYear[breakEvenYear];
            const breakEvenYearAllocation = breakEvenYearPV / breakEvenYearTotalPV;
            const dollarAmountOfBreakEvenYear = breakEvenYearAllocation * remainingAssetsForBreakEvenYear;
            const sumOfPVsBeforeBreakEvenYear = presentValuePerYear.slice(0, breakEvenYear)
                .reduce((previous, current) => previous + current, 0);
            const fundedByPortfolioPercentage = (dollarAmountOfBreakEvenYear + sumOfPVsBeforeBreakEvenYear) * 100 / nonLSGoalRow.presentValue
            nonLSGoalRow.fundedByPortfolioPercentage = Math.round(fundedByPortfolioPercentage);
            return nonLSGoalRow;
        }
        nonLSGoalRow.fundedByPortfolioPercentage = 100;
        return nonLSGoalRow
    })
}

const PrioritizationUtils = {
    makeNonLifestyleGoalListTableRowData,
    addFbniLifestyleGoal,
    getDisplayPresentValueForNonLifestyleGoal,
    getNeeds,
    getWants,
    sortNonLifeStyleGoalList,
    get2dArrayOfPVs,
    getFundedByPortfolioPercentageAttributes,
    sumColumnValues,
    updateNonLSRowsWithFundedByPortfolioPercentage
}

export default PrioritizationUtils;